import { Box } from '@mantine/core';
import { LoginAuthenticationResponse, normalizeOperationOutcome } from '@medplum/core';
import { OperationOutcome } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useEffect, useState, type JSX } from 'react';
import { CustomNewUserForm } from './CustomNewUserForm';
import { setupPremiumTrial } from '../../utils/subscription';
import { gtag_report_conversion } from '../GoogleTagManager';
import { trackCompleteRegistration, trackStartTrial } from '../FacebookPixel';

export interface RegisterFormProps {
  type: 'patient';
  projectId: string;
  clientId?: string;
  recaptchaSiteKey?: string;
  onSuccess: (email?: string) => void;
  onLoading?: (loading: boolean) => void;
  isMobileApp?: boolean;
  redirectUri?: string;
}

export function CustomRegisterForm(props: RegisterFormProps): JSX.Element {
  const { type, projectId, clientId, recaptchaSiteKey, onSuccess, onLoading, isMobileApp, redirectUri } = props;
  const medplum = useMedplum();
  const [login, setLogin] = useState<string>();
  const [outcome, setOutcome] = useState<OperationOutcome>();
  const [registeredEmail, setRegisteredEmail] = useState<string>();

  useEffect(() => {
    onLoading?.(!!outcome);
  }, [outcome, onLoading]);

  useEffect(() => {
    if (type === 'patient' && login) {
      medplum
        .startNewPatient({ 
          login, 
          projectId
        })
        .then((response) => medplum.processCode(response.code as string))
        .then(async () => {
          // Get the newly created patient
          try {
            // Get the patient profile
            const profile = await medplum.getProfile();
            if (profile?.resourceType === 'Patient' && profile?.id) {
              // Set up premium trial
              const patient = await setupPremiumTrial(medplum, profile.id);
              
              // Invalidate the profile cache to pick up the trial extensions
              medplum.invalidateAll();
              
              // Now get the patient to add walkthrough extensions
              const extensions = patient.extension || [];
              
              // Filter out any existing walkthrough extensions
              const filteredExtensions = extensions.filter(e => 
                !e.url.startsWith('https://progressnotes.app/fhir/StructureDefinition/walkthrough-')
              );
              
              // Add walkthrough-related extensions
              const walkthroughExtensions = [
                {
                  url: 'https://progressnotes.app/fhir/StructureDefinition/walkthrough-status',
                  valueString: 'not-started'
                },
                {
                  url: 'https://progressnotes.app/fhir/StructureDefinition/walkthrough-version',
                  valueString: '1.0'
                },
                {
                  url: 'https://progressnotes.app/fhir/StructureDefinition/walkthrough-completed-steps',
                  valueString: '[]'
                }
              ];
              
              // Update the patient with new extensions
              const updatedPatient = await medplum.updateResource({
                ...patient,
                extension: [...filteredExtensions, ...walkthroughExtensions]
              });
              
              console.log('Walkthrough extensions added to new patient:', {
                walkthroughStatus: updatedPatient.extension?.find(e => 
                  e.url === 'https://progressnotes.app/fhir/StructureDefinition/walkthrough-status')?.valueString,
                walkthroughVersion: updatedPatient.extension?.find(e => 
                  e.url === 'https://progressnotes.app/fhir/StructureDefinition/walkthrough-version')?.valueString
              });
              
              // Find trial end date extension safely
              const trialEndDateExt = updatedPatient.extension?.find(e => 
                e.url === 'https://progressnotes.app/fhir/StructureDefinition/trial-end-date');
              
              const trialEndDateValue = trialEndDateExt && 'valueDateTime' in trialEndDateExt ? 
                trialEndDateExt.valueDateTime : undefined;
                
              console.log('Trial extensions added to new patient:', {
                subscriptionStatus: updatedPatient.extension?.find(e => 
                  e.url === 'https://progressnotes.app/fhir/StructureDefinition/subscription-status')?.valueString,
                subscriptionTier: updatedPatient.extension?.find(e => 
                  e.url === 'https://progressnotes.app/fhir/StructureDefinition/subscription-tier')?.valueString,
                trialEndDate: trialEndDateValue
              });
              
              // Track trial start with Facebook Pixel
              trackStartTrial({
                predicted_ltv: 240 // Estimated LTV for trial users
              });
              console.log('Facebook Pixel: Trial start event tracked');
            }
          } catch (error) {
            console.error('Error setting trial extensions:', error);
          }
          
          // Track successful registration as a conversion
          gtag_report_conversion();
          
          // Track registration completion with Facebook Pixel
          trackCompleteRegistration({
            content_name: 'Patient Registration',
            status: 'complete'
          });
          console.log('Facebook Pixel: Registration completion event tracked');
          
          onSuccess(registeredEmail);
        })
        .catch((err) => setOutcome(normalizeOperationOutcome(err)));
    }
  }, [medplum, type, projectId, login, onSuccess, registeredEmail]);

  const handleAuthResponse = (response: LoginAuthenticationResponse, email?: string): void => {
    if (email) {
      setRegisteredEmail(email);
    }
    if (response.code) {
      medplum
        .processCode(response.code)
        .then(() => {
          // Track successful login after registration as a conversion with Google
          gtag_report_conversion();
          
          // Track registration completion with Facebook Pixel
          trackCompleteRegistration({
            content_name: 'Patient Login',
            status: 'complete'
          });
          console.log('Facebook Pixel: Login completion event tracked');
          
          onSuccess(registeredEmail);
        })
        .catch(console.log);
    } else if (response.login) {
      setLogin(response.login);
    }
  };


  return (
    <Box>
      {outcome && <Box c="red" mb="md">{outcome.issue?.[0]?.details?.text || 'An error occurred'}</Box>}
      {!login && (
        <Box>
          <CustomNewUserForm
            projectId={projectId}
            clientId={clientId}
            recaptchaSiteKey={recaptchaSiteKey}
            handleAuthResponse={handleAuthResponse}
            isMobileApp={isMobileApp}
            redirectUri={redirectUri}
          />
        </Box>
      )}
    </Box>
  );
}